import { atom } from 'jotai';

/**
 * 路径历史记录的最大长度
 */
const MAX_HISTORY_LENGTH = 6;

/**
 * 路径历史记录状态
 */
export interface PathHistoryState {
  /** 去重路径历史记录数组，最新的在前面 */
  paths: string[];
  /** 完整路径历史记录数组，包括重复路径，最新的在前面 */
  allPaths: string[];
}

/**
 * 路径历史记录原子
 * 存储用户最近访问的路径，最多保存 6 条
 */
export const pathHistoryAtom = atom<PathHistoryState>({
  paths: [],
  allPaths: [],
});

/**
 * 添加新路径到历史记录
 * 规则：
 * 1. allPaths: 无论是否重复都记录
 * 2. paths: 只有新路径与最后一条路径不同时才添加
 * 3. 将新路径添加到数组开头
 * 4. 保持历史记录不超过 MAX_HISTORY_LENGTH
 */
export const addPathAtom = atom(
  null,
  (get, set, newPath: string) => {
    const currentState = get(pathHistoryAtom);
    const { paths, allPaths } = currentState;

    // allPaths: 无论如何都添加（完整记录）
    const newAllPaths = [newPath, ...allPaths].slice(0, MAX_HISTORY_LENGTH);

    // paths: 只有当前路径与最后一条不同时才添加（去重记录）
    let newPaths = paths;
    if (paths.length === 0 || paths[0] !== newPath) {
      newPaths = [newPath, ...paths].slice(0, MAX_HISTORY_LENGTH);
    }

    set(pathHistoryAtom, {
      paths: newPaths,
      allPaths: newAllPaths,
    });
  }
);

/**
 * 获取上一个访问的路径（当前路径的前一个）- 去重版本
 */
export const previousPathAtom = atom((get) => {
  const { paths } = get(pathHistoryAtom);
  return paths.length > 1 ? paths[1] : null;
});

/**
 * 获取上一个访问的路径（当前路径的前一个）- 完整版本
 */
export const previousPathAllAtom = atom((get) => {
  const { allPaths } = get(pathHistoryAtom);
  return allPaths.length > 1 ? allPaths[1] : null;
});

/**
 * 获取当前路径（最新的路径）
 */
export const currentPathAtom = atom((get) => {
  const { paths } = get(pathHistoryAtom);
  return paths.length > 0 ? paths[0] : null;
});

/**
 * 获取完整路径历史记录（包括重复）
 */
export const allPathsAtom = atom((get) => {
  const { allPaths } = get(pathHistoryAtom);
  return allPaths;
});

/**
 * 获取去重路径历史记录
 */
export const pathsAtom = atom((get) => {
  const { paths } = get(pathHistoryAtom);
  return paths;
});

/**
 * 检查用户是否从指定路径跳转而来（去重版本）
 * @param targetPath 目标路径（可以是完整路径或路径的一部分）
 */
export const isFromPathAtom = atom((get) => (targetPath: string) => {
  const previousPath = get(previousPathAtom);
  if (!previousPath) return false;
  return previousPath.includes(targetPath);
});

/**
 * 检查用户是否从指定路径跳转而来（完整版本）
 * @param targetPath 目标路径（可以是完整路径或路径的一部分）
 */
export const isFromPathAllAtom = atom((get) => (targetPath: string) => {
  const previousPath = get(previousPathAllAtom);
  if (!previousPath) return false;
  return previousPath.includes(targetPath);
});


/**
 * 清空路径历史记录
 */
export const resetHistoryAtom = atom(
  null,
  (_get, set) => {
    set(pathHistoryAtom, {
      paths: [],
      allPaths: [],
    });
  }
);

